var _excluded = ["defaultValue", "disabled", "options", "placeholder", "status", "hint", "hideLabel", "hideStatusIcon", "id", "children", "required", "messages", "onChange", "value", "selectProps", "className", "classes"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import { checkMarkIcon, closeIcon, caratIcon } from '@lululemon/ecom-pattern-library-icons';
import FieldHint from '../internal/field/field-hint';
import Icon from '../icons/index';
require("./native-select.css");
var styles = {
  "statusWrapper": "statusWrapper-2Gdbd",
  "iconContainer": "iconContainer-HkF0K",
  "iconSuccess": "iconSuccess-3S2lZ",
  "iconError": "iconError-19IcO",
  "iconCarat": "iconCarat-IyGZZ",
  "statusWrapperDisabled": "statusWrapperDisabled-5D2i9",
  "iconDisabled": "iconDisabled-1S88e",
  "displayLabel": "displayLabel-2UMv9",
  "labelOptional": "labelOptional-2qiHX",
  "select": "select-2OOlO",
  "status": "status-3k3Ij",
  "disabled": "disabled-2jpTb",
  "iconHidden": "iconHidden-2NNNS",
  "success": "success-3L-x1",
  "warning": "warning-2RiMR",
  "error": "error-20aVF"
};
var NativeSelect = /*#__PURE__*/forwardRef((_ref, ref) => {
  var {
      defaultValue,
      disabled,
      options,
      placeholder,
      status,
      hint,
      hideLabel,
      hideStatusIcon,
      id,
      children,
      required,
      messages,
      onChange,
      value,
      selectProps,
      className,
      classes
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var singleOption = options.length <= 1;
  var inputDisabled = disabled || singleOption;
  var optional = !required && messages && messages.optional ? /*#__PURE__*/React.createElement("span", {
    className: styles.labelOptional
  }, "(", messages.optional, ")") : null;
  return /*#__PURE__*/React.createElement("label", _extends({
    htmlFor: id,
    className: cs(className, classes === null || classes === void 0 ? void 0 : classes.label)
  }, otherProps), /*#__PURE__*/React.createElement("span", {
    className: cs(styles.displayLabel, {
      'lll-hidden-visually': hideLabel
    })
  }, children, " ", optional), /*#__PURE__*/React.createElement("div", {
    className: cs(styles.statusWrapper, {
      [styles.statusWrapperDisabled]: inputDisabled
    })
  }, /*#__PURE__*/React.createElement("select", _extends({
    ref: ref,
    id: id,
    value: value,
    defaultValue: defaultValue,
    "data-testid": id,
    onChange: onChange,
    disabled: inputDisabled,
    className: cs(styles.select, {
      [styles.success]: status === 'success',
      [styles.warning]: status === 'warning',
      [styles.error]: status === 'error',
      [styles.dropdownSingleOption]: singleOption,
      [styles.disabled]: inputDisabled,
      [styles.iconHidden]: hideStatusIcon
    }, classes === null || classes === void 0 ? void 0 : classes.select)
  }, selectProps), !singleOption && /*#__PURE__*/React.createElement("option", {
    value: ""
  }, placeholder), options.map(option => /*#__PURE__*/React.createElement("option", {
    key: option.value,
    value: option.value,
    disabled: option.disabled
  }, option.label))), !hideStatusIcon && status === 'success' && /*#__PURE__*/React.createElement("div", {
    className: cs(styles.iconContainer, styles.iconSuccess, {
      [styles.iconDisabled]: inputDisabled
    })
  }, /*#__PURE__*/React.createElement(Icon, {
    content: checkMarkIcon,
    title: "Success"
  })), !hideStatusIcon && status === 'error' && /*#__PURE__*/React.createElement("div", {
    className: cs(styles.iconContainer, styles.iconError, {
      [styles.iconDisabled]: inputDisabled
    })
  }, /*#__PURE__*/React.createElement(Icon, {
    content: closeIcon,
    title: "Error"
  })), !inputDisabled && /*#__PURE__*/React.createElement(Icon, {
    content: caratIcon,
    className: styles.iconCarat,
    "aria-hidden": "true"
  })), /*#__PURE__*/React.createElement(FieldHint, {
    id: "".concat(id, "-hint"),
    status: status,
    className: classes === null || classes === void 0 ? void 0 : classes.hint
  }, hint));
});
NativeSelect.propTypes = {
  /**
    Default value for uncontrolled usage.
    For controlled use, use "value"
  */
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  placeholder: PropTypes.string,
  status: PropTypes.oneOf(['success', 'warning', 'error']),
  /** Toggle to visually hide label text.
   * Text remains visible to assistive technologies. */
  hideLabel: PropTypes.bool,
  /** Toggle to hide status icon */
  hideStatusIcon: PropTypes.bool,
  /** Hint text for validation messages */
  hint: PropTypes.node,
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  /** The field's label */
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
  /** Localized strings */
  messages: PropTypes.shape({
    /** Label for the "Optional" flag */
    optional: PropTypes.string
  }),
  onChange: PropTypes.func,
  /**
    Value for controled usage.
    For uncontrolled use, use "defaultValue"
  */
  value: PropTypes.string,
  /** pass any props to the element select */
  // eslint-disable-next-line react/forbid-prop-types
  selectProps: PropTypes.object,
  /** Assigns a class to the container element (label). */
  className: PropTypes.string,
  /** Assigns classes to individual elements. */
  classes: PropTypes.shape({
    label: PropTypes.string,
    select: PropTypes.string,
    hint: PropTypes.string
  })
};
NativeSelect.defaultProps = {
  defaultValue: undefined,
  disabled: false,
  hideLabel: false,
  hideStatusIcon: false,
  hint: null,
  messages: {},
  name: null,
  onChange: null,
  placeholder: null,
  required: true,
  status: null,
  value: undefined,
  selectProps: {},
  className: null,
  classes: {}
};
export default NativeSelect;